import { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  getApiV5FirmsDocumentsDocumentUuidAnnotation,
  getGetApiV5FirmsDocumentsDocumentUuidAnnotationQueryKey,
  putApiV5FirmsDocumentsDocumentUuidAnnotation,
} from '@liscio/api';
import { normalizeInstantJson } from '@liscio/ui';

export type DocumentAnnotationSource = {
  load: () => Promise<Record<string, unknown> | null>;
  /** Takes unknown because PSPDFKit's AnnotationsSource hands it an untyped export. */
  save: (instantJson: unknown) => Promise<void>;
};

/**
 * Returns a stable annotation source for the shared viewer, or undefined when the
 * document UUID isn't known yet. Safe to pass straight through to the viewer —
 * identity is memoized on documentUuid.
 */
export function useDocumentAnnotationsSource(
  documentUuid: string | null | undefined,
): DocumentAnnotationSource | undefined {
  const queryClient = useQueryClient();

  return useMemo(() => {
    if (!documentUuid) return undefined;

    const key =
      getGetApiV5FirmsDocumentsDocumentUuidAnnotationQueryKey(documentUuid);

    return {
      load: async () => {
        const response = await queryClient.fetchQuery({
          queryKey: key,
          queryFn: () =>
            getApiV5FirmsDocumentsDocumentUuidAnnotation(documentUuid),
        });
        return normalizeInstantJson(response.data?.instant_json);
      },
      save: async (instantJson) => {
        // If the input isn't a recognizable object, skip the write rather
        // than overwrite the server's state with an empty blob.
        const safePayload = normalizeInstantJson(instantJson);
        if (!safePayload) return;
        const response = await putApiV5FirmsDocumentsDocumentUuidAnnotation(
          documentUuid,
          { instant_json: safePayload },
        );
        queryClient.setQueryData(key, response);
      },
    };
  }, [documentUuid, queryClient]);
}
